import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { UPDATE_EVENT } from '../lib/buildWatch';

/**
 * UpdateToast — "Pear has been updated. Welcome back."
 *
 * Card 7bLlo0He2hW0: the app-update / cache-bust signal is a JitsLab-style
 * bottom toast (UpdateBanner, web-v2) rather than a hard reload that throws
 * away whatever the reader was looking at.
 *
 * Detection is deliberately passive: the bundle knows its own `__BUILD_ID__`,
 * localStorage remembers the build from the last visit, and a difference means
 * this navigation handed over a newer build than the one they left. That is
 * the honest moment for "has been updated" — past tense, the update already
 * happened — and it needs no extra endpoint, no service-worker plumbing and
 * no reload. First visit has nothing to compare against, so it stays quiet.
 *
 * 2026-10-17, the founder's "cache buster that doesn't rely on site refresh":
 * buildWatch re-reads index.html while the app is OPEN. It never reloads the
 * page on its own (pre-deploy review 4): a build landing is an OFFER, and this
 * toast is the way out — same copy in both paths, plus a Reload button when the
 * watcher found the new bundle. Nothing half-written is traded for an
 * up-to-date bundle, and the exact-string assertion in walk-ui-batch-1010 stays
 * true whether the notice came from a build-id change or from buildWatch.
 *
 * Portalled to body: the app root carries the fade-in transform, and a
 * transformed ancestor is the containing block for `position: fixed` (the
 * FktjQ7CT4keo lesson — same reason KudosToast does not mount in-panel).
 */
const BUILD_KEY = 'pear.build';
const UPDATE_MESSAGE = 'Pear has been updated. Welcome back.';

function readBuild(): string {
  return typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : 'dev';
}

export function UpdateToast() {
  const [visible, setVisible] = useState(false);
  const [offerReload, setOfferReload] = useState(false);

  /* Card bThYhNAz0zWC (founder email 2026-10-21: "App has been updated toast
     should auto dismiss after ~5 seconds if possible"): the notice states a
     fact the reader can act on, and then gets out of the way on its own. Five
     seconds is long enough to read the line and register the Reload offer and
     short enough that it never becomes furniture. The X still dismisses at
     once — the timer only replaces the case where nobody touches it. Keyed on
     `visible` so a re-show (buildWatch after the toast was dismissed) gets a
     fresh window instead of inheriting a spent one, and cleared on unmount so
     the app never calls setState on a dead component. */
  useEffect(() => {
    if (!visible) return;
    const t = window.setTimeout(() => setVisible(false), 5000);
    return () => window.clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    const current = readBuild();
    let previous: string | null = null;
    try {
      previous = window.localStorage.getItem(BUILD_KEY);
      window.localStorage.setItem(BUILD_KEY, current);
    } catch {
      // Storage blocked (private mode) — we cannot tell, so we stay silent.
      return;
    }
    if (previous && previous !== current) setVisible(true);
  }, []);

  /* buildWatch's path: a deploy arrived mid-session and there is a draft. */
  useEffect(() => {
    const onAvailable = () => {
      setOfferReload(true);
      setVisible(true);
    };
    window.addEventListener(UPDATE_EVENT, onAvailable);
    return () => window.removeEventListener(UPDATE_EVENT, onAvailable);
  }, []);

  if (!visible) return null;

  return createPortal(
    <div
      className="pear-update-toast"
      role="status"
      data-testid="update-toast"
      data-build={readBuild()}
    >
      <span className="pear-update-toast-text">{UPDATE_MESSAGE}</span>
      {offerReload && (
        <button
          type="button"
          className="pear-update-toast-reload"
          data-testid="update-reload"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      )}
      <button
        type="button"
        className="pear-update-toast-dismiss"
        aria-label="Dismiss update notice"
        onClick={() => setVisible(false)}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
